import { FC, useEffect } from 'react';
import {
  Button,
  DataLossPrompt,
  FileInput,
  FileLink,
  Footer,
  ImageList,
  MultiLanguageInputContainer,
  Panel,
  RichTextInput,
  SelectInput,
  SortableList,
  TextInput,
} from 'melp-design/components';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContentLanguage, contentLanguages } from 'types/general';
import { APP, ROUTES } from 'config';
import {
  Control,
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { Translations } from 'types/Common';
import { useRouteParams } from 'utils/useIdParam';
import { formatLanguage } from 'utils/format';
import { useUploadFile } from 'store/files';
import { Delete } from '@mui/icons-material';
import { SystemColors } from 'melp-design/style';
import { usePredefinedToasts } from 'utils/Toast';
import { upper } from 'utils/general';
import {
  useAdminBenefitTemplate,
  useAdminUpdateBenefitTemplate,
} from 'store/admin-benefit-templates';

interface Values {
  languages: Array<Uppercase<ContentLanguage>>;
  imageId: string;
  translations: Translations<{
    title: string;
    description: string;
    attachments: Array<{ fileId: string }>;
    links: Array<{ name: string; url: string; index: number }>;
    faq: Array<{ question: string; answer: string; index: number }>;
  }>;
}

const INITIAL_VALUES = {
  languages: [upper(APP.locales.default)],
  imageId: '',
  translations: [
    {
      language: upper(APP.locales.default),
      title: '',
      description: '',
      attachments: [],
      links: [],
      faq: [],
    },
  ],
};

const INITIAL_LINK: Values['translations'][0]['links'][number] = {
  index: 0,
  name: '',
  url: '',
};
const INITIAL_FAQ: Values['translations'][0]['faq'][number] = {
  index: 0,
  question: '',
  answer: '',
};

const AttachmentsList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();

  const {
    fields: attachmentFields,
    append: appendAttachment,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.attachments`,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  return (
    <Controller
      name={`translations.${translationIndex}.attachments`}
      control={control}
      render={({ field: { name, value, onChange, ref } }) => (
        <Stack gap={1}>
          <FileInput
            name={name}
            onChange={([file]) => {
              if (file) {
                uploadFile(
                  { file },
                  {
                    onSuccess: ({ data }) => {
                      appendAttachment({ fileId: data.id });
                    },
                  },
                );
              } else {
                onChange(value);
              }
            }}
            disabled={isUploading}
            maxFiles={1}
            previewSelectedFiles={false}
            accept={[
              'jpg',
              'jpeg',
              'png',
              'gif',
              'webp',
              'txt',
              'pdf',
              'doc',
              'docx',
              'xls',
              'xlsx',
            ]}
            ref={ref}
          />

          <SortableList
            key={JSON.stringify(attachmentFields.map((field) => field.id))}
            disabled
            items={attachmentFields.map((field, i) => ({
              id: field.id,
              content: (
                <Stack
                  direction="row"
                  alignItems="center"
                  py={0.5}
                  px={2}
                  spacing={2}
                  sx={{ backgroundColor: SystemColors.white }}
                  borderRadius={1}
                  boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                  key={field.id}
                >
                  <Box flex={1}>
                    <FileLink id={field.fileId} />
                  </Box>

                  <Box flexShrink={0}>
                    <Button
                      variant="neutral-outline"
                      size="md"
                      icon={Delete}
                      onClick={() => removeAttachment(i)}
                      title={t('common.remove')}
                    />
                  </Box>
                </Stack>
              ),
            }))}
          />
        </Stack>
      )}
    />
  );
};

const LinksList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();

  const {
    fields: linksFields,
    append: appendLinks,
    remove: removeLinks,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.links`,
  });

  return (
    <Stack gap={1}>
      <SortableList
        key={JSON.stringify(linksFields.map((field) => field.id))}
        disabled
        items={linksFields.map((field, i) => ({
          id: field.id,
          content: (
            <Stack
              direction="row"
              alignItems="center"
              p={1}
              spacing={2}
              sx={{ backgroundColor: SystemColors.white }}
              borderRadius={1}
              boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
              key={field.id}
            >
              <Stack flex={1} gap={1}>
                <Controller
                  name={`translations.${translationIndex}.links.${i}.name`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.link_title')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name={`translations.${translationIndex}.links.${i}.url`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.link_url')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Box flexShrink={0}>
                <Button
                  variant="neutral-outline"
                  size="md"
                  icon={Delete}
                  onClick={() => removeLinks(i)}
                  title={t('common.remove')}
                />
              </Box>
            </Stack>
          ),
        }))}
      />

      <Box>
        <Button
          label={t('buttons.add_link')}
          variant="tertiary"
          onClick={() => {
            appendLinks(INITIAL_LINK);
          }}
        />
      </Box>
    </Stack>
  );
};

const FaqList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();

  const {
    fields: faqFields,
    append: appendFaq,
    move: moveFaq,
    remove: removeFaq,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.faq`,
  });

  return (
    <Stack gap={1}>
      <SortableList
        key={JSON.stringify(faqFields.map((field) => field.id))}
        onPositionChange={moveFaq}
        items={faqFields.map((field, i) => ({
          id: field.id,
          content: (
            <Stack
              direction="row"
              alignItems="center"
              p={1}
              spacing={2}
              sx={{ backgroundColor: SystemColors.white }}
              borderRadius={1}
              boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
              key={field.id}
            >
              <SortableList.DragIndicator />

              <Stack flex={1} gap={1}>
                <Controller
                  name={`translations.${translationIndex}.faq.${i}.question`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('surveys.question')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name={`translations.${translationIndex}.faq.${i}.answer`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.answer')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      rows={5}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Box flexShrink={0}>
                <Button
                  variant="neutral-outline"
                  size="md"
                  icon={Delete}
                  onClick={() => removeFaq(i)}
                  title={t('common.remove')}
                />
              </Box>
            </Stack>
          ),
        }))}
      />

      <Box>
        <Button
          label={t('surveys.addQuestion')}
          variant="tertiary"
          onClick={() => {
            appendFaq(INITIAL_FAQ);
          }}
        />
      </Box>
    </Stack>
  );
};

export const BenefitTemplateContent: FC = () => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { id: benefitTemplateId } = useRouteParams(
    ROUTES.admin.benefitTemplates.details,
  );

  const { data: template } = useAdminBenefitTemplate(benefitTemplateId);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (template && template.translations.length) {
      reset({
        languages: template.translations.map((trans) => trans.language),
        imageId: template.translations[0].image?.id ?? '',
        translations: template.translations.map((trans) => ({
          language: trans.language,
          title: trans.title,
          description: trans.description,
          attachments: trans.attachments.map((attachment) => ({
            fileId: attachment.fileId,
          })),
          links: trans.links.map((link) => ({
            index: link.index,
            name: link.name,
            url: link.url,
          })),
          faq: trans.faq.map((f) => ({
            question: f.question,
            answer: f.answer,
            index: f.index,
          })),
        })),
      });
    }
  }, [template, reset]);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: updateTemplate, isLoading: isUpdating } =
    useAdminUpdateBenefitTemplate();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateTemplate(
      {
        benefitTemplateId,
        data: {
          translations: values.translations.map((trans) => ({
            ...trans,
            imageId: values.imageId || null,
          })),
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  const [languages] = watch(['languages']);
  const isDirty = !!Object.keys(dirtyFields).length;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('benefitTemplates.templateContent')}>
            <Stack spacing={2}>
              <Controller
                name="languages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('companies.supported_languages')}
                    name={name}
                    value={value}
                    onChange={(value) => {
                      const selectedLanguages = value as Array<
                        Uppercase<ContentLanguage>
                      >;
                      replaceTranslations(
                        selectedLanguages.map(
                          (language) =>
                            getValues('translations').find(
                              (t) => t.language === language,
                            ) ?? {
                              ...INITIAL_VALUES.translations[0],
                              language,
                            },
                        ),
                      );
                      onChange(selectedLanguages);
                    }}
                    options={contentLanguages.map((l) => {
                      const language = l.toUpperCase();
                      return {
                        value: language,
                        label: formatLanguage(i18n.language, language),
                      };
                    })}
                    multiple
                    required
                    ref={ref}
                  />
                )}
              />

              {languages.length ? (
                <>
                  <MultiLanguageInputContainer
                    label={t('benefits.title')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.title`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <TextInput
                              name={name}
                              value={value}
                              onChange={onChange}
                              required
                              ai={{
                                inputLanguage: upper(APP.locales.default),
                                outputLanguage: field.language,
                                text:
                                  getValues('translations').find(
                                    (t) =>
                                      t.language === upper(APP.locales.default),
                                  )?.title ?? '',
                              }}
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />

                  <MultiLanguageInputContainer
                    label={t('form.description')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.description`}
                          control={control}
                          render={({ field: { value, onChange, ref } }) => (
                            <RichTextInput
                              value={value}
                              onChange={onChange}
                              minHeight={250}
                              maxHeight={500}
                              ai={{
                                inputLanguage: upper(APP.locales.default),
                                outputLanguage: field.language,
                                text:
                                  getValues('translations').find(
                                    (t) =>
                                      t.language === upper(APP.locales.default),
                                  )?.description ?? '',
                              }}
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />

                  <Controller
                    name="imageId"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return value ? (
                        <ImageList
                          items={[
                            {
                              imageId: value,
                              actions: [
                                {
                                  icon: Delete,
                                  variant: 'neutral-outline',
                                  onClick: () => onChange(''),
                                },
                              ],
                            },
                          ]}
                        />
                      ) : (
                        <FileInput
                          label={t('form.image')}
                          name={name}
                          onChange={([file]) => {
                            if (file) {
                              uploadFile(
                                { file },
                                {
                                  onSuccess: ({ data }) => {
                                    onChange(data.id);
                                  },
                                },
                              );
                            } else {
                              onChange('');
                            }
                          }}
                          disabled={isUploading}
                          maxFiles={1}
                          previewSelectedFiles={false}
                          accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </>
              ) : null}
            </Stack>
          </Panel>

          <Panel title={t('form.attachments')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <AttachmentsList control={control} translationIndex={i} />
                  ),
                }))}
              />
            </Stack>
          </Panel>

          <Panel title={t('benefits.pdfLinks')}>
            <Stack>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <LinksList control={control} translationIndex={i} />
                  ),
                }))}
              />
            </Stack>
          </Panel>

          <Panel title={t('form.faq')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: <FaqList control={control} translationIndex={i} />,
                }))}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={isUpdating}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
